import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { toast } from "sonner";
import { Call as TwilioCall } from "@twilio/voice-sdk";
import { usePowerDialerSocket } from "@/hooks/usePowerDialerSocket";
import { useCallDialog } from "@/context/CallDialogContext";
import {
  useStartCampaignMutation,
  useStopCampaignMutation,
  useHoldCampaignCallMutation,
  useResumeCampaignCallMutation,
  useDropCampaignVoicemailMutation,
  useEndCampaignCallMutation,
  useSetCampaignDispositionMutation,
  useSaveCampaignNotesMutation,
  useStartDialerSessionMutation,
  useEndDialerSessionMutation,
  usePreviewDialNextMutation,
  useSkipCampaignContactMutation,
  useLazyGetDialerSessionSnapshotQuery,
  useMarkDialerSessionReadyMutation,
  useStartDialerSessionBreakMutation,
  useTakeDialerReturnCallMutation,
  useHangUpDialerReturnCallMutation,
  useUpdateCampaignMutation,
  useCreateTaskMutation,
  useGetHomeQuery,
  restApi,
} from "@/store/rest_api";
import type Contact from "@/model/interface/Contact";
import type CampaignContact from "@/model/interface/CampaignContact";
import type { CampaignContactStatus } from "@/model/interface/CampaignContact";
import Campaign, { CampaignFormData, CampaignSettings, CampaignStatus, CampaignType, DialingMode } from "@/model/interface/Campaign";
import type { PowerDialerEvent } from "@/model/interface/PowerDialerEvent";
import type PhoneNumber from "@/model/interface/PhoneNumber";
import type { DialerReturnCallInfo, DialerSessionSnapshot, DialerSessionStatus } from "@/model/interface/DialerSession";
import { getContactDisplayName } from "@/util/string";
import { apiErrorMessage, apiErrorStatus } from "@/util/apiError";
import { DIALER_ROOM_PREFIX, LINE_REFRESH_AFTER_MS } from "@/util/dialer";
import { applyPreferredMicrophone } from "@/components/dialer/util/MicrophoneUtil";
import { DEVICE_PLATFORM } from "@/util/device";
import { NotesSaveState } from "@/enum/NotesSaveState";

export interface PowerDialerCall {
  id: string;
  contact: Contact;
  phoneNumber: string;
  status: CampaignContactStatus;
  conferenceName?: string;
  call?: { id: string };
  duration: number;
  disposition?: string;
  notes?: string;
  exitReason?: string;
  answeredAt?: string;
  connectedAt?: string;
  connectedByUserId?: string;
  amdResult?: string;
  retryCount?: number;
  nextRetryAt?: string;
  sequence?: number;
  sessionId?: string;
  leadTimeZone?: string;
}

interface PowerDialerContextType {
  isActive: boolean;
  campaign: Campaign | null;
  settings: CampaignSettings | null;
  isPreview: boolean;
  activate: (campaign: Campaign) => void;
  deactivate: () => void;

  isDialing: boolean;
  stopReason: string | null;
  sessionId: string | null;
  sessionStatus: DialerSessionStatus | null;
  markReady: () => Promise<void>;
  takeBreak: () => Promise<void>;
  isSessionActionPending: boolean;
  returnCall: DialerReturnCallInfo | null;
  takeReturnCall: () => Promise<void>;
  hangUpReturnCall: () => Promise<void>;
  isReturnCallActionPending: boolean;
  isEndingAfterCall: boolean;
  startDialing: (acknowledgedRegistration?: boolean) => Promise<void>;
  stopDialing: () => Promise<void>;
  endSession: (options: { afterCurrentCall: boolean; onEnded?: () => void }) => Promise<void>;
  lines: number;
  updateLines: (lines: number) => void;
  isUpdatingLines: boolean;
  updateCallerId: (phoneNumber: PhoneNumber) => Promise<void>;

  holdCall: (campaignContact: { id: string }) => void;
  resumeCall: (campaignContact: { id: string }) => void;
  dropVoicemail: (campaignContact: { id: string }) => void;
  isCallActionPending: boolean;
  endCall: (campaignContact: { id: string }) => Promise<boolean>;
  selectDisposition: (campaignContact: { id: string }, disposition: string) => void;
  saveOutcome: (campaignContact: { id: string }) => Promise<boolean>;
  saveAndNext: (campaignContact: { id: string }) => Promise<void>;
  setNotes: (campaignContact: { id: string }, notes: string) => void;
  notesSaveStates: Record<string, NotesSaveState>;

  callLeadAnyway: (campaignContact: { id: string }, acknowledgedCallingRules: boolean) => Promise<string | null>;
  skipContact: (campaignContact: { id: string }) => void;

  wrapUpCall: PowerDialerCall | null;
  completeWrapUp: (disposition: string | null, notes: string, createFollowUpTask: boolean) => Promise<void>;
  dismissWrapUp: () => void;

  agentVoiceCall: TwilioCall | null;
  registerAgentCall: (call: TwilioCall) => void;

  roomCall: TwilioCall | null;
  isLineOpen: boolean;
  calls: PowerDialerCall[];
  lineCalls: PowerDialerCall[];
  dialingCalls: PowerDialerCall[];
  answeredCalls: PowerDialerCall[];
  activeCall: PowerDialerCall | undefined;
}

const PowerDialerContext = createContext<PowerDialerContextType | undefined>(undefined);

const UNKNOWN_CONTACT: Contact = { id: "", firstName: "Unknown" } as Contact;

const ENDED_STATUSES: CampaignContactStatus[] = [
  "COMPLETED",
  "FAILED",
  "NO_ANSWER",
  "BUSY",
  "VOICEMAIL",
  "ABANDONED",
  "CANCELED",
  "SUPPRESSED",
  "SKIPPED",
];

const LIVE_STATUSES: CampaignContactStatus[] = ["ANSWERED", "CONNECTED", "ON_HOLD"];

const BRIDGED_STATUSES: CampaignContactStatus[] = ["CONNECTED", "ON_HOLD"];

const LINE_STATUSES: CampaignContactStatus[] = ["DIALING", "RINGING", "ANSWERED", "CONNECTED", "ON_HOLD"];

const NOTES_SAVE_DELAY_MS = 1000;

const LINE_RETRY_DELAY_MS = 3000;

const LINE_REFRESH_CHECK_MS = 30_000;

const QUEUE_REFRESH_DELAY_MS = 1500;

function contactFromEvent(event: PowerDialerEvent): Contact {
  return {
    id: event.contactId ?? "",
    firstName: event.contactFirstName ?? "Unknown",
    lastName: event.contactLastName,
    company: event.contactCompany,
    photo: event.contactPhoto,
    phones: event.contactPhones?.map((phone) => ({
      id: phone.id,
      phoneNumber: phone.phoneNumber,
      phoneNumberE164: phone.phoneNumberE164,
      status: phone.status,
      doNotCall: phone.doNotCall,
      litigator: phone.litigator,
      dncListStatus: phone.dncListStatus,
    })),
  } as Contact;
}

function secondsSince(timestamp?: string): number {
  if (!timestamp) {
    return 0;
  }
  return Math.max(0, Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000));
}

function mapCampaignContactToCall(cc: CampaignContact): PowerDialerCall {
  return {
    id: cc.id,
    contact: cc.contact ?? UNKNOWN_CONTACT,
    phoneNumber: cc.phoneNumber ?? "",
    status: cc.status,
    conferenceName: cc.conferenceName,
    call: cc.call?.id ? { id: cc.call.id } : undefined,
    duration: LIVE_STATUSES.includes(cc.status) ? secondsSince(cc.answeredAt) : 0,
    disposition: cc.disposition,
    notes: cc.notes,
    exitReason: cc.exitReason,
    answeredAt: cc.answeredAt,
    connectedAt: cc.connectedAt,
    connectedByUserId: cc.connectedByUserId,
    amdResult: cc.amdResult,
    retryCount: cc.retryCount,
    nextRetryAt: cc.nextRetryAt,
    sequence: cc.sequence,
    sessionId: cc.sessionId,
    leadTimeZone: cc.leadTimeZone ?? undefined,
  };
}

function isOnSession(call: PowerDialerCall, sessionId: string | null): boolean {
  return sessionId !== null && call.sessionId === sessionId;
}

function liveReturnCall(returnCall?: DialerReturnCallInfo | null): DialerReturnCallInfo | null {
  if (!returnCall || (returnCall.status !== "RINGING" && returnCall.status !== "CONNECTED")) {
    return null;
  }
  return returnCall;
}

function campaignToFormData(campaign: Campaign, settings: CampaignSettings): CampaignFormData {
  return {
    name: campaign.name ?? "",
    description: campaign.description ?? "",
    type: campaign.type ?? CampaignType.POWER_DIALER,
    dialingMode: campaign.dialingMode ?? DialingMode.POWER,
    phoneNumber: campaign.phoneNumber?.id ? { id: campaign.phoneNumber.id } : undefined,
    holdRecordingUrl: campaign.holdRecordingUrl,
    settings,
    aiAgent: campaign.aiAgent,
    workflow: campaign.workflowId ? { id: campaign.workflowId } : undefined,
    targetType: campaign.targetType,
    targetRef: campaign.targetRef,
    maxEnrollmentsPerDay: campaign.maxEnrollmentsPerDay,
    stopOnReply: campaign.stopOnReply,
    scrubDnc: campaign.scrubDnc,
    continuous: campaign.continuous,
  };
}

export function PowerDialerProvider({ children }: { children: React.ReactNode }) {
  const [isActive, setIsActive] = useState(false);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [calls, setCalls] = useState<PowerDialerCall[]>([]);
  const [isDialing, setIsDialing] = useState(false);
  const [stopReason, setStopReason] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionStatus, setSessionStatus] = useState<DialerSessionStatus | null>(null);
  const [isSessionActionPending, setIsSessionActionPending] = useState(false);
  const [isEndingAfterCall, setIsEndingAfterCall] = useState(false);
  const [wrapUpCallId, setWrapUpCallId] = useState<string | null>(null);
  const [isCallActionPending, setIsCallActionPending] = useState(false);
  const [agentVoiceCall, setAgentVoiceCall] = useState<TwilioCall | null>(null);
  const [roomCall, setRoomCall] = useState<TwilioCall | null>(null);
  const [isLineOpen, setIsLineOpen] = useState(false);
  const [lineAttempt, setLineAttempt] = useState(0);
  const [notesSaveStates, setNotesSaveStates] = useState<Record<string, NotesSaveState>>({});
  const [returnCall, setReturnCall] = useState<DialerReturnCallInfo | null>(null);
  const [isReturnCallActionPending, setIsReturnCallActionPending] = useState(false);

  const timersRef = useRef<Record<string, ReturnType<typeof setInterval>>>({});
  const campaignRef = useRef<Campaign | null>(null);
  const callsRef = useRef<PowerDialerCall[]>([]);
  const currentUserIdRef = useRef<string | null>(null);
  const roomCallRef = useRef<TwilioCall | null>(null);
  const roomJoinedAtRef = useRef<number>(0);
  const isJoiningRoomRef = useRef(false);
  const hasWarnedLineRef = useRef(false);
  const queueRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedOutcomesRef = useRef<Record<string, { disposition: string; notes: string }>>({});
  const sessionIdRef = useRef<string | null>(null);
  const sessionStatusRef = useRef<DialerSessionStatus | null>(null);
  const skipWrapUpIdsRef = useRef<Set<string>>(new Set());
  const wrapUpCallIdRef = useRef<string | null>(null);
  const endAfterWrapUpRef = useRef<(() => void) | null>(null);
  const notesTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const pendingNotesRef = useRef<Record<string, { campaignId: string; callId?: string; notes: string }>>({});
  const notesQueueRef = useRef<Record<string, Promise<void>>>({});
  const eventSequenceRef = useRef<number | null>(null);
  const isResyncingRef = useRef(false);
  const isResyncQueuedRef = useRef(false);
  const pendingEventsRef = useRef<(() => void)[]>([]);
  const dispatch = useDispatch();
  const { device } = useCallDialog();

  const { data: currentUser } = useGetHomeQuery();
  const [startCampaignMut] = useStartCampaignMutation();
  const [stopCampaignMut] = useStopCampaignMutation();
  const [holdCallMut] = useHoldCampaignCallMutation();
  const [resumeCallMut] = useResumeCampaignCallMutation();
  const [dropVoicemailMut] = useDropCampaignVoicemailMutation();
  const [endCallMut] = useEndCampaignCallMutation();
  const [setDispositionMut] = useSetCampaignDispositionMutation();
  const [saveCampaignNotes] = useSaveCampaignNotesMutation();
  const [startDialerSession] = useStartDialerSessionMutation();
  const [endDialerSession] = useEndDialerSessionMutation();
  const [previewDialMut] = usePreviewDialNextMutation();
  const [skipContactMut] = useSkipCampaignContactMutation();
  const [updateCampaignMut, updateCampaignResult] = useUpdateCampaignMutation();
  const [createTaskMut] = useCreateTaskMutation();
  const [fetchSnapshot] = useLazyGetDialerSessionSnapshotQuery();
  const [markSessionReadyMut] = useMarkDialerSessionReadyMutation();
  const [startSessionBreakMut] = useStartDialerSessionBreakMutation();
  const [takeReturnCallMut] = useTakeDialerReturnCallMutation();
  const [hangUpReturnCallMut] = useHangUpDialerReturnCallMutation();

  const settings = campaign?.settings ?? null;
  const isPreview = campaign?.dialingMode === DialingMode.PREVIEW;
  const lines = settings?.callsPerAgent ?? 3;

  useEffect(() => {
    callsRef.current = calls;
  }, [calls]);

  useEffect(() => {
    currentUserIdRef.current = currentUser?.id ?? null;
  }, [currentUser]);

  useEffect(() => {
    wrapUpCallIdRef.current = wrapUpCallId;
  }, [wrapUpCallId]);

  const startTimer = useCallback((id: string) => {
    if (timersRef.current[id]) return;
    timersRef.current[id] = setInterval(() => {
      setCalls((prev) => prev.map((c) => (c.id === id ? { ...c, duration: c.duration + 1 } : c)));
    }, 1000);
  }, []);

  const stopTimer = useCallback((id: string) => {
    if (timersRef.current[id]) {
      clearInterval(timersRef.current[id]);
      delete timersRef.current[id];
    }
  }, []);

  const stopAllTimers = useCallback(() => {
    Object.keys(timersRef.current).forEach((id) => clearInterval(timersRef.current[id]));
    timersRef.current = {};
  }, []);

  // ---- Agent voice leg ----

  const registerAgentCall = useCallback((call: TwilioCall) => {
    setAgentVoiceCall(call);
    const clear = () => setAgentVoiceCall((current) => (current === call ? null : current));
    call.on("disconnect", clear);
    call.on("cancel", clear);
    call.on("reject", clear);
  }, []);

  const sendNotes = useCallback(
    async (campaignContactId: string) => {
      const pending = pendingNotesRef.current[campaignContactId];
      if (!pending) {
        return;
      }
      delete pendingNotesRef.current[campaignContactId];
      setNotesSaveStates((previous) => ({ ...previous, [campaignContactId]: NotesSaveState.SAVING }));
      try {
        await saveCampaignNotes({
          campaign: { id: pending.campaignId },
          campaignContact: { id: campaignContactId },
          callId: pending.callId,
          notes: pending.notes,
        }).unwrap();
        setNotesSaveStates((previous) => ({ ...previous, [campaignContactId]: NotesSaveState.SAVED }));
      } catch {
        setNotesSaveStates((previous) => ({ ...previous, [campaignContactId]: NotesSaveState.FAILED }));
      }
    },
    [saveCampaignNotes],
  );

  const flushNotes = useCallback(
    (campaignContactId: string) => {
      const pendingTimer = notesTimersRef.current[campaignContactId];
      if (pendingTimer) {
        clearTimeout(pendingTimer);
        delete notesTimersRef.current[campaignContactId];
      }
      const previousSave = notesQueueRef.current[campaignContactId] ?? Promise.resolve();
      const nextSave = previousSave.then(() => sendNotes(campaignContactId));
      notesQueueRef.current[campaignContactId] = nextSave;
      return nextSave;
    },
    [sendNotes],
  );

  const hangUpRoom = useCallback(() => {
    const currentRoomCall = roomCallRef.current;
    roomCallRef.current = null;
    setRoomCall(null);
    setIsLineOpen(false);
    currentRoomCall?.disconnect();
  }, []);

  const joinRoom = useCallback(
    async (liveSessionId: string) => {
      if (!device || roomCallRef.current || isJoiningRoomRef.current) {
        return;
      }
      isJoiningRoomRef.current = true;
      try {
        await applyPreferredMicrophone(device);
        const call = await device.connect({
          params: { conference: `${DIALER_ROOM_PREFIX}${liveSessionId}`, platform: DEVICE_PLATFORM },
        });
        if (sessionIdRef.current !== liveSessionId) {
          call.disconnect();
          setLineAttempt((attempt) => attempt + 1);
          return;
        }
        roomCallRef.current = call;
        roomJoinedAtRef.current = Date.now();
        setRoomCall(call);
        call.on("accept", () => {
          hasWarnedLineRef.current = false;
          setIsLineOpen(true);
        });
        call.on("disconnect", () => {
          if (roomCallRef.current !== call) {
            return;
          }
          roomCallRef.current = null;
          setRoomCall(null);
          setIsLineOpen(false);
          setTimeout(() => setLineAttempt((attempt) => attempt + 1), LINE_RETRY_DELAY_MS);
        });
      } catch (error) {
        if (!hasWarnedLineRef.current) {
          hasWarnedLineRef.current = true;
          toast.error(apiErrorMessage(error, "Could not connect your line. Trying again."));
        }
        setTimeout(() => setLineAttempt((attempt) => attempt + 1), LINE_RETRY_DELAY_MS);
      } finally {
        isJoiningRoomRef.current = false;
      }
    },
    [device],
  );

  const applySessionStatus = useCallback((status: DialerSessionStatus | null) => {
    sessionStatusRef.current = status;
    setSessionStatus(status);
  }, []);

  const rememberSession = useCallback(
    (nextSessionId: string | null) => {
      if (sessionIdRef.current !== nextSessionId) {
        eventSequenceRef.current = null;
        applySessionStatus(null);
        hangUpRoom();
      }
      sessionIdRef.current = nextSessionId;
      setSessionId(nextSessionId);
    },
    [hangUpRoom, applySessionStatus],
  );

  const ensureSession = useCallback(async () => {
    const currentCampaign = campaignRef.current;
    if (!currentCampaign) {
      return null;
    }
    try {
      const session = await startDialerSession({ campaign: { id: currentCampaign.id } }).unwrap();
      rememberSession(session.id);
      return session.id;
    } catch (error) {
      rememberSession(null);
      toast.error(apiErrorMessage(error, "Could not start a dialer session"));
      return null;
    }
  }, [startDialerSession, rememberSession]);

  const endSessionNow = useCallback(async () => {
    const endingSessionId = sessionIdRef.current;
    if (!endingSessionId) {
      return;
    }
    rememberSession(null);
    try {
      await endDialerSession({ sessionId: endingSessionId, reason: "ENDED_BY_AGENT" }).unwrap();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not end the dialer session"));
    }
  }, [endDialerSession, rememberSession]);

  const endSession = useCallback(
    async ({ afterCurrentCall, onEnded }: { afterCurrentCall: boolean; onEnded?: () => void }) => {
      const myId = currentUserIdRef.current;
      const onACall = callsRef.current.some(
        (call) => call.status === "CONNECTED" && (!myId || call.connectedByUserId === myId),
      );
      if (afterCurrentCall && (onACall || wrapUpCallIdRef.current !== null)) {
        endAfterWrapUpRef.current = onEnded ?? (() => undefined);
        setIsEndingAfterCall(true);
        return;
      }
      await endSessionNow();
      onEnded?.();
    },
    [endSessionNow],
  );

  // ---- WebSocket ----

  const followStatus = useCallback(
    (previous: PowerDialerCall | undefined, campaignContactId: string, status: CampaignContactStatus) => {
      if (LIVE_STATUSES.includes(status)) {
        startTimer(campaignContactId);
      }
      if (!ENDED_STATUSES.includes(status) && status !== "QUEUED") {
        return;
      }
      stopTimer(campaignContactId);

      const myId = currentUserIdRef.current;
      const wasMyCall =
        previous !== undefined &&
        BRIDGED_STATUSES.includes(previous.status) &&
        (!myId || previous.connectedByUserId === myId);
      if (wasMyCall && ENDED_STATUSES.includes(status) && status !== "VOICEMAIL") {
        if (skipWrapUpIdsRef.current.delete(campaignContactId)) {
          return;
        }
        setWrapUpCallId(campaignContactId);
      }
    },
    [startTimer, stopTimer],
  );

  const applyEvent = useCallback(
    (event: PowerDialerEvent) => {
      const status = event.status;
      const ccId = event.campaignContactId;
      const previous = callsRef.current.find((c) => c.id === ccId);

      setCalls((prev) => {
        const exists = prev.some((c) => c.id === ccId);

        if (exists) {
          return prev.map((c) => {
            if (c.id !== ccId) return c;
            return {
              ...c,
              status,
              call: event.callId ? { id: event.callId } : c.call,
              conferenceName: event.conferenceName ?? c.conferenceName,
              answeredAt: event.answeredAt ?? c.answeredAt,
              connectedAt: event.connectedAt ?? c.connectedAt,
              disposition: event.disposition ?? c.disposition,
              notes: event.notes ?? c.notes,
              exitReason: event.exitReason ?? c.exitReason,
              connectedByUserId: event.connectedByUserId,
              amdResult: event.amdResult ?? c.amdResult,
              retryCount: event.retryCount ?? c.retryCount,
              nextRetryAt: event.nextRetryAt,
              sessionId: event.sessionId,
              leadTimeZone: event.leadTimeZone ?? c.leadTimeZone,
              duration: status === "QUEUED" ? 0 : c.duration,
            };
          });
        }

        return [
          ...prev,
          {
            id: ccId,
            contact: contactFromEvent(event),
            phoneNumber: event.phoneNumber,
            status,
            conferenceName: event.conferenceName,
            call: event.callId ? { id: event.callId } : undefined,
            duration: 0,
            disposition: event.disposition,
            notes: event.notes,
            exitReason: event.exitReason,
            answeredAt: event.answeredAt,
            connectedAt: event.connectedAt,
            connectedByUserId: event.connectedByUserId,
            amdResult: event.amdResult,
            retryCount: event.retryCount,
            nextRetryAt: event.nextRetryAt,
            sequence: event.sequence,
            sessionId: event.sessionId,
            leadTimeZone: event.leadTimeZone,
          },
        ];
      });

      followStatus(previous, ccId, status);
    },
    [followStatus],
  );

  const refreshQueueSoon = useCallback(() => {
    if (queueRefreshTimerRef.current) {
      return;
    }
    queueRefreshTimerRef.current = setTimeout(() => {
      queueRefreshTimerRef.current = null;
      dispatch(restApi.util.invalidateTags(["DialerQueue", "DialerPreview"]));
    }, QUEUE_REFRESH_DELAY_MS);
  }, [dispatch]);

  const applyResync = useCallback(
    (snapshot: DialerSessionSnapshot | null) => {
      const merged = (snapshot?.leads ?? []).map(mapCampaignContactToCall);
      const previousById = new Map(callsRef.current.map((call) => [call.id, call]));

      stopAllTimers();
      callsRef.current = merged;
      setCalls(merged);
      merged.forEach((call) => {
        const previous = previousById.get(call.id);
        if (LIVE_STATUSES.includes(call.status) || (previous !== undefined && previous.status !== call.status)) {
          followStatus(previous, call.id, call.status);
        }
      });
      dispatch(restApi.util.invalidateTags(["DialerQueue"]));
      setReturnCall(liveReturnCall(snapshot?.returnCall));

      if (!snapshot) {
        return;
      }
      eventSequenceRef.current = snapshot.eventSequence;
      applySessionStatus(snapshot.session.status);
      const isCampaignActive = snapshot.campaignStatus === CampaignStatus.ACTIVE;
      setIsDialing(isCampaignActive);
      setStopReason(isCampaignActive ? null : (snapshot.stopReason ?? null));
      setCampaign((current) => (current ? { ...current, status: snapshot.campaignStatus } : current));
      if (campaignRef.current) {
        campaignRef.current = { ...campaignRef.current, status: snapshot.campaignStatus };
      }
    },
    [stopAllTimers, followStatus, dispatch, applySessionStatus],
  );

  const resync = useCallback(async () => {
    const syncingCampaign = campaignRef.current;
    if (!syncingCampaign) {
      return;
    }
    if (isResyncingRef.current) {
      isResyncQueuedRef.current = true;
      return;
    }
    isResyncingRef.current = true;
    const syncingSessionId = sessionIdRef.current;
    try {
      const snapshot = syncingSessionId ? await fetchSnapshot(syncingSessionId).unwrap() : null;
      if (campaignRef.current?.id === syncingCampaign.id && sessionIdRef.current === syncingSessionId) {
        applyResync(snapshot);
      }
    } catch (error) {
      console.error("Failed to resync the power dialer:", error);
    } finally {
      isResyncingRef.current = false;
      const pendingEvents = pendingEventsRef.current;
      pendingEventsRef.current = [];
      pendingEvents.forEach((replay) => replay());
      if (isResyncQueuedRef.current) {
        isResyncQueuedRef.current = false;
        void resync();
      }
    }
  }, [fetchSnapshot, applyResync]);

  const handleCampaignEvent = useCallback(
    (event: PowerDialerEvent) => {
      if (!campaignRef.current || event.campaignId !== campaignRef.current.id) {
        return;
      }
      if (isResyncingRef.current) {
        pendingEventsRef.current.push(() => {
          handleCampaignEvent(event);
        });
        return;
      }

      refreshQueueSoon();
      if (event.type !== "pd_campaign_stopped") {
        return;
      }
      const reason = event.reason ?? "Dialing stopped";
      setIsDialing(false);
      setStopReason(reason);
      setCampaign((prev) => (prev ? { ...prev, status: CampaignStatus.PAUSED } : prev));
      campaignRef.current = { ...campaignRef.current, status: CampaignStatus.PAUSED };
      toast.error(reason);
    },
    [refreshQueueSoon],
  );

  const handleSessionEvent = useCallback(
    (event: PowerDialerEvent) => {
      if (event.sessionId !== sessionIdRef.current) {
        return;
      }
      if (isResyncingRef.current) {
        pendingEventsRef.current.push(() => {
          handleSessionEvent(event);
        });
        return;
      }
      const eventSequence = event.eventSequence ?? 0;
      const lastSequence = eventSequenceRef.current;
      if (lastSequence !== null && eventSequence <= lastSequence) {
        return;
      }
      eventSequenceRef.current = eventSequence;
      if (event.type === "pd_session_state") {
        applySessionStatus(event.sessionStatus ?? null);
      } else if (event.type === "pd_return_call") {
        setReturnCall(liveReturnCall(event.returnCall));
      } else {
        applyEvent(event);
        refreshQueueSoon();
      }
      if (lastSequence !== null && eventSequence > lastSequence + 1) {
        void resync();
      }
    },
    [applyEvent, resync, refreshQueueSoon, applySessionStatus],
  );

  usePowerDialerSocket(campaign?.id ?? null, sessionId, {
    onCampaignEvent: handleCampaignEvent,
    onSessionEvent: handleSessionEvent,
    onConnected: ensureSession,
    onSessionSubscribed: resync,
  });

  useEffect(() => {
    if (!sessionId || !device) {
      return;
    }
    void joinRoom(sessionId);
  }, [sessionId, device, lineAttempt, joinRoom]);

  useEffect(() => {
    if (!roomCall) {
      return;
    }
    const refreshTimer = setInterval(() => {
      const isLineBusy = callsRef.current.some(
        (call) => LINE_STATUSES.includes(call.status) && isOnSession(call, sessionIdRef.current),
      );
      if (isLineBusy || Date.now() - roomJoinedAtRef.current < LINE_REFRESH_AFTER_MS) {
        return;
      }
      hangUpRoom();
      setLineAttempt((attempt) => attempt + 1);
    }, LINE_REFRESH_CHECK_MS);
    return () => clearInterval(refreshTimer);
  }, [roomCall, hangUpRoom]);

  // ---- Session lifecycle ----

  const activate = useCallback(
    async (newCampaign: Campaign) => {
      setCampaign(newCampaign);
      campaignRef.current = newCampaign;
      setIsActive(true);
      setCalls([]);
      setWrapUpCallId(null);
      setIsDialing(newCampaign.status === CampaignStatus.ACTIVE);
      setStopReason(null);
      savedOutcomesRef.current = {};
      skipWrapUpIdsRef.current.clear();
      setNotesSaveStates({});

      await ensureSession();
      await resync();
    },
    [ensureSession, resync],
  );

  const deactivate = useCallback(() => {
    stopAllTimers();
    Object.keys(notesTimersRef.current).forEach((campaignContactId) => {
      flushNotes(campaignContactId);
    });
    endAfterWrapUpRef.current = null;
    setIsEndingAfterCall(false);
    void endSessionNow();
    setIsActive(false);
    setCampaign(null);
    campaignRef.current = null;
    setCalls([]);
    setWrapUpCallId(null);
    setIsDialing(false);
    savedOutcomesRef.current = {};
    setNotesSaveStates({});
    setAgentVoiceCall(null);
    setReturnCall(null);
  }, [stopAllTimers, flushNotes, endSessionNow]);

  const startDialing = useCallback(async (acknowledgedRegistration: boolean = false) => {
    if (!campaignRef.current) return;
    const liveSessionId = await ensureSession();
    if (!liveSessionId) {
      return;
    }
    try {
      await startCampaignMut({ campaign: { id: campaignRef.current.id }, acknowledgedRegistration }).unwrap();
      setIsDialing(true);
      setStopReason(null);
      setCampaign((prev) => (prev ? { ...prev, status: CampaignStatus.ACTIVE } : prev));
      if (campaignRef.current) {
        campaignRef.current = { ...campaignRef.current, status: CampaignStatus.ACTIVE };
      }
    } catch (e: any) {
      toast.error(e?.detail ?? "Failed to start campaign");
    }
  }, [ensureSession, startCampaignMut]);

  const stopDialing = useCallback(async () => {
    if (!campaignRef.current) return;
    try {
      await stopCampaignMut({ campaign: { id: campaignRef.current.id } }).unwrap();
      setIsDialing(false);
      setCampaign((prev) => (prev ? { ...prev, status: CampaignStatus.PAUSED } : prev));
      if (campaignRef.current) {
        campaignRef.current = { ...campaignRef.current, status: CampaignStatus.PAUSED };
      }
    } catch (e: any) {
      toast.error(e?.detail ?? "Failed to stop campaign");
    }
  }, [stopCampaignMut]);

  const updateLines = useCallback(
    async (newLines: number) => {
      const current = campaignRef.current;
      if (!current) return;
      const currentSettings = current.settings ?? ({} as CampaignSettings);
      const nextSettings = { ...currentSettings, callsPerAgent: newLines };
      try {
        await updateCampaignMut({ id: current.id, data: campaignToFormData(current, nextSettings) }).unwrap();
        const updated = { ...current, settings: nextSettings };
        campaignRef.current = updated;
        setCampaign(updated);
      } catch (e: any) {
        toast.error(e?.detail ?? "Failed to update line count");
      }
    },
    [updateCampaignMut],
  );

  const updateCallerId = useCallback(
    async (phoneNumber: PhoneNumber) => {
      const current = campaignRef.current;
      if (!current || !phoneNumber.id) {
        return;
      }
      try {
        await updateCampaignMut({
          id: current.id,
          data: {
            ...campaignToFormData(current, current.settings ?? ({} as CampaignSettings)),
            phoneNumber: { id: phoneNumber.id },
          },
        }).unwrap();
        const updated = { ...current, phoneNumber };
        campaignRef.current = updated;
        setCampaign(updated);
        void resync();
      } catch (error) {
        toast.error(apiErrorMessage(error, "Could not change the caller ID"));
      }
    },
    [updateCampaignMut, resync],
  );

  const markReady = useCallback(async () => {
    const readySessionId = sessionIdRef.current;
    if (!readySessionId) {
      return;
    }
    setIsSessionActionPending(true);
    try {
      await markSessionReadyMut({ sessionId: readySessionId }).unwrap();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not get ready for the next call"));
    } finally {
      setIsSessionActionPending(false);
    }
  }, [markSessionReadyMut]);

  const takeBreak = useCallback(async () => {
    const breakSessionId = sessionIdRef.current;
    if (!breakSessionId) {
      return;
    }
    setIsSessionActionPending(true);
    try {
      await startSessionBreakMut({ sessionId: breakSessionId }).unwrap();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not start your break"));
    } finally {
      setIsSessionActionPending(false);
    }
  }, [startSessionBreakMut]);

  const takeReturnCall = useCallback(async () => {
    const takingSessionId = sessionIdRef.current;
    if (!takingSessionId || !returnCall) {
      return;
    }
    setIsReturnCallActionPending(true);
    try {
      const taken = await takeReturnCallMut({
        sessionId: takingSessionId,
        returnCallId: returnCall.returnCallId,
      }).unwrap();
      setReturnCall(liveReturnCall(taken));
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not take the call"));
      if (apiErrorStatus(error) === 404) {
        setReturnCall(null);
      }
    } finally {
      setIsReturnCallActionPending(false);
    }
  }, [returnCall, takeReturnCallMut]);

  const hangUpReturnCall = useCallback(async () => {
    const hangUpSessionId = sessionIdRef.current;
    if (!hangUpSessionId || !returnCall) {
      return;
    }
    setIsReturnCallActionPending(true);
    try {
      await hangUpReturnCallMut({ sessionId: hangUpSessionId, returnCallId: returnCall.returnCallId }).unwrap();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not hang up"));
    } finally {
      setIsReturnCallActionPending(false);
    }
  }, [returnCall, hangUpReturnCallMut]);

  // ---- Call controls ----

  const holdCall = useCallback(
    async (campaignContact: { id: string }) => {
      if (!campaignRef.current) {
        return;
      }
      setIsCallActionPending(true);
      try {
        await holdCallMut({ campaign: { id: campaignRef.current.id }, campaignContact }).unwrap();
      } catch (error) {
        toast.error(apiErrorMessage(error, "Could not put the lead on hold"));
      } finally {
        setIsCallActionPending(false);
      }
    },
    [holdCallMut],
  );

  const resumeCall = useCallback(
    async (campaignContact: { id: string }) => {
      if (!campaignRef.current) {
        return;
      }
      setIsCallActionPending(true);
      try {
        await resumeCallMut({ campaign: { id: campaignRef.current.id }, campaignContact }).unwrap();
      } catch (error) {
        toast.error(apiErrorMessage(error, "Could not take the lead off hold"));
      } finally {
        setIsCallActionPending(false);
      }
    },
    [resumeCallMut],
  );

  const dropVoicemail = useCallback(
    async (campaignContact: { id: string }) => {
      if (!campaignRef.current) {
        return;
      }
      setIsCallActionPending(true);
      try {
        await dropVoicemailMut({ campaign: { id: campaignRef.current.id }, campaignContact }).unwrap();
      } catch (error) {
        toast.error(apiErrorMessage(error, "Could not leave the voicemail message"));
      } finally {
        setIsCallActionPending(false);
      }
    },
    [dropVoicemailMut],
  );

  const endCall = useCallback(
    async (campaignContact: { id: string }) => {
      if (!campaignRef.current) {
        return false;
      }
      try {
        await endCallMut({ campaign: { id: campaignRef.current.id }, campaignContact }).unwrap();
        stopTimer(campaignContact.id);
        return true;
      } catch (error) {
        toast.error(apiErrorMessage(error, "Failed to end call"));
        return false;
      }
    },
    [endCallMut, stopTimer],
  );

  const selectDisposition = useCallback((campaignContact: { id: string }, disposition: string) => {
    setCalls((prev) => prev.map((call) => (call.id === campaignContact.id ? { ...call, disposition } : call)));
  }, []);

  const persistOutcome = useCallback(
    async (campaignContactId: string, disposition: string, notes: string) => {
      const currentCampaign = campaignRef.current;
      if (!currentCampaign) {
        return false;
      }
      const saved = savedOutcomesRef.current[campaignContactId];
      if (saved && saved.disposition === disposition && saved.notes === notes) {
        return true;
      }
      try {
        await setDispositionMut({
          campaign: { id: currentCampaign.id },
          campaignContact: { id: campaignContactId },
          disposition,
          notes: notes || undefined,
        }).unwrap();
        savedOutcomesRef.current[campaignContactId] = { disposition, notes };
        setCalls((prev) =>
          prev.map((call) =>
            call.id === campaignContactId ? { ...call, disposition, notes: notes || call.notes } : call,
          ),
        );
        return true;
      } catch (error) {
        toast.error(apiErrorMessage(error, "Failed to save the disposition"));
        return false;
      }
    },
    [setDispositionMut],
  );

  const saveOutcome = useCallback(
    async (campaignContact: { id: string }) => {
      await flushNotes(campaignContact.id);
      const call = callsRef.current.find((candidate) => candidate.id === campaignContact.id);
      if (!call?.disposition) {
        return true;
      }
      return persistOutcome(call.id, call.disposition, call.notes ?? "");
    },
    [flushNotes, persistOutcome],
  );

  const setNotes = useCallback(
    (campaignContact: { id: string }, notes: string) => {
      setCalls((prev) => prev.map((call) => (call.id === campaignContact.id ? { ...call, notes } : call)));
      const currentCampaign = campaignRef.current;
      if (!currentCampaign) {
        return;
      }
      const callId = callsRef.current.find((candidate) => candidate.id === campaignContact.id)?.call?.id;
      pendingNotesRef.current[campaignContact.id] = { campaignId: currentCampaign.id, callId, notes };
      const pendingTimer = notesTimersRef.current[campaignContact.id];
      if (pendingTimer) {
        clearTimeout(pendingTimer);
      }
      notesTimersRef.current[campaignContact.id] = setTimeout(() => {
        flushNotes(campaignContact.id);
      }, NOTES_SAVE_DELAY_MS);
    },
    [flushNotes],
  );

  // ---- Preview mode ----

  const callLeadAnyway = useCallback(
    async (campaignContact: { id: string }, acknowledgedCallingRules: boolean) => {
      if (!campaignRef.current) {
        return null;
      }
      try {
        await previewDialMut({
          campaign: { id: campaignRef.current.id },
          campaignContact,
          acknowledgedCallingRules,
        }).unwrap();
        return null;
      } catch (error) {
        if (apiErrorStatus(error) === 409) {
          return apiErrorMessage(error, "This lead can't be called right now.");
        }
        toast.error(apiErrorMessage(error, "Could not call this lead"));
        return null;
      }
    },
    [previewDialMut],
  );

  const skipContact = useCallback(
    async (campaignContact: { id: string }) => {
      if (!campaignRef.current) return;
      try {
        await skipContactMut({ campaign: { id: campaignRef.current.id }, campaignContact }).unwrap();
      } catch (e: any) {
        toast.error(e?.data?.detail ?? e?.detail ?? "Failed to skip contact");
      }
    },
    [skipContactMut],
  );

  // ---- Wrap-up ----

  const wrapUpCall = useMemo(
    () => calls.find((c) => c.id === wrapUpCallId) ?? null,
    [calls, wrapUpCallId],
  );

  const finishWrapUp = useCallback(async () => {
    const finishEnding = endAfterWrapUpRef.current;
    if (!finishEnding) {
      if (sessionStatusRef.current !== "BREAK") {
        await markReady();
      }
      return;
    }
    endAfterWrapUpRef.current = null;
    setIsEndingAfterCall(false);
    await endSessionNow();
    finishEnding();
  }, [endSessionNow, markReady]);

  const completeWrapUp = useCallback(
    async (disposition: string | null, notes: string, createFollowUpTask: boolean) => {
      const call = callsRef.current.find((candidate) => candidate.id === wrapUpCallId);
      if (!call || !campaignRef.current) {
        setWrapUpCallId(null);
        return;
      }
      await flushNotes(call.id);
      if (disposition) {
        await persistOutcome(call.id, disposition, notes);
      }
      if (createFollowUpTask && call.contact.id) {
        const dueDate = new Date();
        dueDate.setDate(dueDate.getDate() + 1);
        try {
          await createTaskMut({
            title: `Follow up with ${getContactDisplayName(call.contact) ?? call.phoneNumber}`,
            description: notes || undefined,
            dueDate: dueDate.toISOString(),
            contact: { id: call.contact.id } as Contact,
          }).unwrap();
        } catch (error) {
          toast.error(apiErrorMessage(error, "Failed to create the follow-up task"));
        }
      }
      setWrapUpCallId(null);
      await finishWrapUp();
    },
    [wrapUpCallId, flushNotes, persistOutcome, createTaskMut, finishWrapUp],
  );

  const dismissWrapUp = useCallback(() => {
    setWrapUpCallId(null);
    void finishWrapUp();
  }, [finishWrapUp]);

  const saveAndNext = useCallback(
    async (campaignContact: { id: string }) => {
      const isSaved = await saveOutcome(campaignContact);
      if (isSaved) {
        skipWrapUpIdsRef.current.add(campaignContact.id);
      }
      const isEnded = await endCall(campaignContact);
      if (!isEnded) {
        skipWrapUpIdsRef.current.delete(campaignContact.id);
        return;
      }
      if (isSaved) {
        await finishWrapUp();
      }
    },
    [saveOutcome, endCall, finishWrapUp],
  );

  // ---- Derived ----

  const dialingCalls = useMemo(
    () =>
      calls.filter(
        (call) => (call.status === "DIALING" || call.status === "RINGING") && isOnSession(call, sessionId),
      ),
    [calls, sessionId],
  );
  const answeredCalls = useMemo(
    () => calls.filter((call) => call.status === "ANSWERED" && isOnSession(call, sessionId)),
    [calls, sessionId],
  );
  const activeCall = useMemo(
    () => calls.find((call) => BRIDGED_STATUSES.includes(call.status) && isOnSession(call, sessionId)),
    [calls, sessionId],
  );
  const lineCalls = useMemo(
    () => calls.filter((call) => LINE_STATUSES.includes(call.status) && isOnSession(call, sessionId)),
    [calls, sessionId],
  );

  const value = useMemo<PowerDialerContextType>(
    () => ({
      isActive,
      campaign,
      settings,
      isPreview,
      activate,
      deactivate,
      isDialing,
      stopReason,
      sessionId,
      sessionStatus,
      markReady,
      takeBreak,
      isSessionActionPending,
      returnCall,
      takeReturnCall,
      hangUpReturnCall,
      isReturnCallActionPending,
      isEndingAfterCall,
      startDialing,
      stopDialing,
      endSession,
      lines,
      updateLines,
      isUpdatingLines: updateCampaignResult.isLoading,
      updateCallerId,
      holdCall,
      resumeCall,
      dropVoicemail,
      isCallActionPending,
      endCall,
      selectDisposition,
      saveOutcome,
      saveAndNext,
      setNotes,
      notesSaveStates,
      callLeadAnyway,
      skipContact,
      wrapUpCall,
      completeWrapUp,
      dismissWrapUp,
      agentVoiceCall,
      registerAgentCall,
      roomCall,
      isLineOpen,
      calls,
      lineCalls,
      dialingCalls,
      answeredCalls,
      activeCall,
    }),
    [
      isActive,
      campaign,
      settings,
      isPreview,
      activate,
      deactivate,
      isDialing,
      stopReason,
      sessionId,
      sessionStatus,
      markReady,
      takeBreak,
      isSessionActionPending,
      returnCall,
      takeReturnCall,
      hangUpReturnCall,
      isReturnCallActionPending,
      isEndingAfterCall,
      startDialing,
      stopDialing,
      endSession,
      lines,
      updateLines,
      updateCampaignResult.isLoading,
      updateCallerId,
      holdCall,
      resumeCall,
      dropVoicemail,
      isCallActionPending,
      endCall,
      selectDisposition,
      saveOutcome,
      saveAndNext,
      setNotes,
      notesSaveStates,
      callLeadAnyway,
      skipContact,
      wrapUpCall,
      completeWrapUp,
      dismissWrapUp,
      agentVoiceCall,
      registerAgentCall,
      roomCall,
      isLineOpen,
      calls,
      lineCalls,
      dialingCalls,
      answeredCalls,
      activeCall,
    ],
  );

  return <PowerDialerContext.Provider value={value}>{children}</PowerDialerContext.Provider>;
}

export function usePowerDialer() {
  const context = useContext(PowerDialerContext);
  if (context === undefined) {
    throw new Error("usePowerDialer must be used within a PowerDialerProvider");
  }
  return context;
}
